<template>
  <div
    v-if="!authStore.profile || isProcessing"
    class="py-10 flex justify-center"
  >
    <BaseSpinner />
  </div>
  <template v-else-if="ordersStore.order">
    <LayoutWithRightSidebar show-right-sidebar-on-mobile breakpoint="xl">
      <template #rightSidebar>
        <OrdersRigthSidebar
          :order="ordersStore.order"
          :user-email="authStore.profile.email"
          :loading="isPaymentLinkLoading"
          @pay="submit"
        />
      </template>

      <div
        class="flex flex-col gap-6 sm:gap-6 bg-white rounded-xl px-4 py-6 sm:p-8"
      >
        <div class="flex justify-between items-center">
          <h1 class="font-bold text-2xl">
            {{ event?.name }}
          </h1>
          <BaseLink
            look="link"
            theme="light"
            class="font-normal hidden sm:inline-flex"
            size="xs"
            :to="{ name: 'users-me-orders' }"
          >
            <BaseIcon name="outline_arrow_turn_left" size="xs" class="mr-2" />
            <span class="text-sm">
              {{ $t('tickets.back_to_orders') }}
            </span>
          </BaseLink>
        </div>
        <div v-if="ticketsToActivateCount">
          <OrdersRemaining :count="ticketsToActivateCount" />
        </div>
        <OrdersCard
          class="bg-primary-50"
          :order="ordersStore.order"
          :event-name="event?.name"
          :event-route="eventRoute"
          :is-show-icon-block="!!ticketsToActivateCount"
        />
        <template v-if="event">
          <template v-for="item in ordersStore.order.items" :key="item.id">
            <OrdersItemCard
              v-if="'ticket' in item.details"
              :item="(item as any)"
              :currency="ordersStore.order.currency"
              :is-paid="isOrderPaid"
              :event-route="eventRoute"
              :order-id="ordersStore.order.id"
            />
            <OrdersItemPackageCard
              v-else-if="'sponsor_package' in item.details"
              :item="(item as any)"
              :currency="ordersStore.order.currency"
              :is-paid="isOrderPaid"
              :event-route="eventRoute"
              :order-id="ordersStore.order.id"
            />
          </template>
        </template>
      </div>
    </LayoutWithRightSidebar>
  </template>
  <PaymentXSolla
    ref="XSollaComponent"
    @done="onPaymentDone"
    @close="onPaymentClose"
  />
  <PaymentStripe
    ref="StripeComponent"
    :order-id="id"
    :total="stripeValue"
    @done="onPaymentDone"
    @close="onPaymentClose"
  />
  <PaymentCloudPayments
    ref="CloudPaymentsComponent"
    @done="onPaymentDone"
    @close="onPaymentClose"
  />
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { OrderStatus } from '~/models/orders'
import type { OrderResponse } from '~/models/orders'
import { useAuth } from '~/stores/auth'
import { useOrders } from '~/stores/orders'
import { useEvents } from '~/stores/events'
import { useNotifications } from '~/stores/notifications'
import { PaymentSystem } from '~/models/payment'
import PaymentXSolla from '~/components/payment/PaymentXSolla.vue'
import PaymentStripe from '~/components/payment/PaymentStripe.vue'
import { languageMap } from '~/models/locale'
import PaymentCloudPayments from '~/components/payment/PaymentCloudPayments.vue'
import { useECommercePayment } from '~/composables/use-ecommerce-payment'

const CloudPaymentsComponent = ref<null | InstanceType<
  typeof PaymentCloudPayments
>>(null)
const StripeComponent = ref<null | InstanceType<typeof PaymentStripe>>(null)
const XSollaComponent = ref<null | InstanceType<typeof PaymentXSolla>>(null)

const authStore = useAuth()
const eventsStore = useEvents()
const ordersStore = useOrders()
const {
  fetchOrder,
  countOrderItemsToActivate,
  getOrderPaymentLink,
  formatPrice,
  fetchItemsToActivateCount,
} = ordersStore
const route = useRoute()
const { t, locale } = useI18n()
const { pushNotification } = useNotifications()
const { processOrderSuccess } = useECommercePayment()
const router = useRouter()

const isProcessing = ref(false)
const isPaymentLinkLoading = ref(false)
const paymentLink = ref<string | null>(null)

const id = Number(route.params.id)
const isNavigated = ref(false)
let statusTimer: any

const isSuccess = ref(false)

const stripeValue = computed(() => {
  return orderResponse.value?.amount
    ? formatPrice(
        ((orderResponse.value?.special_price || orderResponse.value.amount) +
          (orderResponse.value?.vat || 0)) /
          100,
        orderResponse.value?.currency,
        languageMap[locale.value]
      )
    : ''
})

const load = async (reload = false, withLoader = true) => {
  try {
    isProcessing.value = withLoader
    await fetchOrder(id, reload)

    if (ordersStore.order) {
      await eventsStore.fetchEventsByIds(ordersStore.order.event_id)
    }

    if (process.client && isSuccess.value) {
      clearTimeout(statusTimer)
      if (ordersStore.order?.status === OrderStatus.Created) {
        statusTimer = setTimeout(() => {
          load(true, false)
        }, 2000)
      } else if (ordersStore.order?.status === OrderStatus.Done) {
        await Promise.all([
          processOrderSuccess(ordersStore.order, event.value),
          fetchItemsToActivateCount(),
        ])
        pushNotification({ title: t('notifications.tickets.order_success') })
        isSuccess.value = false
      }
    }
  } catch (error: any) {
    if (error.statusCode === 404) {
      throw useCreateScreenError({ message: 'order is not found' })
    } else if (error.statusCode === 403) {
      // if user opens a link with someone else's order
      await navigateTo(useLocaleRoute()('/users/me/orders'))
    } else {
      throw error
    }
  } finally {
    isProcessing.value = false
  }
}

const ticketsToActivateCount = computed(() => {
  return ordersStore.order ? countOrderItemsToActivate(ordersStore.order) : 0
})

const isOrderPaid = computed(
  () => ordersStore.order?.status === OrderStatus.Done
)

const event = computed(() => {
  return ordersStore.order?.event_id
    ? eventsStore.eventsMap[ordersStore.order.event_id]
    : undefined
})

const eventRoute = computed(() =>
  event.value
    ? eventsStore.getEventPageRoute(event.value.slug, event.value.type)
    : ''
)

const orderResponse = ref<null | Partial<OrderResponse>>(null)

function onPaymentClose() {
  isPaymentLinkLoading.value = false
  isNavigated.value = false
}

async function onPaymentDone() {
  onPaymentClose()
  isSuccess.value = true
  await load(true)
}

const submit = async () => {
  if (!ordersStore.order) {
    return
  }

  isPaymentLinkLoading.value = true
  try {
    orderResponse.value = await getOrderPaymentLink(ordersStore.order.id)
    paymentLink.value = orderResponse.value?.payment_link || ''

    if (orderResponse.value?.payment_system_type === PaymentSystem.XSolla) {
      isNavigated.value = true

      await XSollaComponent.value?.processXSollaOrder(
        orderResponse.value?.payment_system_data?.token as string
      )
      return
    }
    if (orderResponse.value?.payment_system_type === PaymentSystem.Stripe) {
      isNavigated.value = true

      await StripeComponent.value?.processStripePayment(
        orderResponse.value?.payment_system_data?.token as string
      )
      return
    }
    if (
      orderResponse.value?.payment_system_type === PaymentSystem.CloudPayments
    ) {
      isNavigated.value = true

      const price =
        orderResponse.value?.special_price || orderResponse.value?.amount || 0

      if (!price) {
        throw new Error('Order amount is not defined on CloudPayments payment')
      }

      await CloudPaymentsComponent.value?.processCloudPayments({
        amount: price + (orderResponse.value?.vat || 0),
        invoiceId: String(orderResponse.value?.id),
        description: event.value?.name,
      })
    }
  } catch (error) {
    pushNotification({
      title: t('notifications.tickets.create_url_error'),
      theme: 'destructive',
    })
    throw error
  } finally {
    !isNavigated.value && (isPaymentLinkLoading.value = false)
  }
}

onServerPrefetch(async () => {
  await load()
})

onMounted(async () => {
  if (route.query.status === 'success') {
    isSuccess.value = true
  }

  await load(false, ordersStore.order?.id !== id)

  if (route.query.status === 'success') {
    router.replace({
      query: { ...route.query, status: undefined },
    })
  }
})

onUnmounted(() => {
  clearTimeout(statusTimer)
})
</script>
